import { desktopRecordingBridge, type DesktopRecordingBridge } from '@/lib/desktop-recording';
import { CaptureError } from '../contracts';
import { createDesktopPcmStream } from './desktop-pcm';

/** Native bridge lifetime follows the audio track, including partial-start failure. */
export async function openDesktopAudio(
  context: AudioContext,
  bridge: DesktopRecordingBridge | null = desktopRecordingBridge(),
): Promise<MediaStream> {
  if (!bridge) throw new CaptureError('native_unavailable');
  let id: string | null = null;
  let stream: MediaStream | null = null;
  let pcm: ReturnType<typeof createDesktopPcmStream> | null = null;
  let closed = false;
  let terminal = false;
  const earlyFrames: Array<{ id: string; data: ArrayBuffer }> = [];
  let earlyBytes = 0;
  const earlyEnded = new Set<string>();
  const fail = () => {
    terminal = true;
    for (const track of stream?.getAudioTracks() ?? []) {
      track.stop();
      track.dispatchEvent(new Event('ended'));
    }
  };
  const push = (frame: { id: string; data: ArrayBuffer }) => {
    if (closed) return;
    if (!id || !pcm) {
      earlyBytes += frame.data.byteLength;
      if (earlyBytes > 384000) {
        terminal = true;
        return;
      }
      earlyFrames.push(frame);
    } else if (frame.id === id) {
      try {
        pcm.push(frame.data);
      } catch {
        fail();
      }
    }
  };
  const unframe = bridge.onSystemAudioFrame(push);
  const unstate = bridge.onSystemAudioState((state) => {
    if (!['error', 'ended'].includes(state.state)) return;
    if (!id) earlyEnded.add(state.id);
    else if (state.id === id) fail();
  });
  const release = () => {
    if (closed) return;
    closed = true;
    unframe();
    unstate();
    pcm?.stop();
    earlyFrames.length = 0;
    if (id) void bridge.stopSystemAudio(id).catch(() => {});
  };
  try {
    const started = await bridge.startSystemAudio();
    if (!started.ok)
      throw new CaptureError(
        started.error === 'consent_required' ? 'native_permission_denied' : 'native_unavailable',
      );
    id = started.id;
    if (earlyEnded.has(id)) throw new CaptureError('native_unavailable');
    if (started.method === 'pcm' && started.format) {
      pcm = createDesktopPcmStream(context, started.format);
      stream = pcm.stream;
      for (const frame of earlyFrames) if (frame.id === id) pcm.push(frame.data);
      earlyFrames.length = 0;
    } else if (started.method === 'loopback') {
      // The native app grants one audio request. The required video track is
      // discarded here, before the shared audio recorder sees the stream.
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        // Remote voices are already processed by the call application. A second
        // speech filter can suppress them or fade sustained sounds out.
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      for (const track of stream.getVideoTracks()) {
        track.stop();
        stream.removeTrack(track);
      }
    } else throw new CaptureError('native_unavailable');
    if (terminal || !stream.getAudioTracks().length) throw new CaptureError('native_unavailable');
    for (const track of stream.getAudioTracks()) {
      const stop = track.stop.bind(track);
      track.stop = () => {
        stop();
        release();
      };
      track.addEventListener('ended', release, { once: true });
    }
    return stream;
  } catch (error) {
    for (const track of stream?.getTracks() ?? []) track.stop();
    release();
    throw error instanceof CaptureError
      ? error
      : new CaptureError(
          error instanceof DOMException && error.name === 'NotAllowedError'
            ? 'native_permission_denied'
            : 'native_unavailable',
        );
  }
}
